import { Component, OnDestroy, OnInit } from '@angular/core';
import { AccountSplitResource, ManagementFeesAccountSplitsResource, Organization } from 'src/app/client-api.service';
import { OrganizationService } from 'src/app/core-services/organization.service';
import { AccountSplitService, ManagementFeesAccountSplit } from '../services/account-splits.service';
import { map, tap } from 'rxjs/operators';
import { combineLatest } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { TableButton, TableColumn } from 'src/app/shared/tcc-ui/tables';


@Component({
  selector: 'app-management-fee-account-splits',
  templateUrl: './management-fee-account-splits.component.html',
  styleUrls: ['./management-fee-account-splits.component.scss'
  ]
})
export class ManagementFeeAccountSplitsComponent implements OnInit, OnDestroy {
  
  accountSplits$ = this.accountSplitService.accountSplits$;
  accountSplits: AccountSplitResource[] | undefined;

  addMode = false;
  buttons: TableButton<any>[] | undefined;
  columns: TableColumn<AccountSplitResource>[] | undefined;
  
  isBalanced = false;
  expsTotal = 0;
  revsTotal = 0;

  managementFeeAccountSplits$ = this.accountSplitService.ManagementFeeAccountSplits$
  managementFeeAccountSplits: ManagementFeesAccountSplitsResource[] | undefined;

  filteredAccountSplits: AccountSplitResource[] | undefined;
  orgs$ = this.orgSvc.orgs$;

  selectedOrg: Organization | undefined;
  selectedAccountSplit:  AccountSplitResource | undefined;  


  // placeholder for something potential -  same thing in the management-fee-service - seems like someone was prepering for something here
  splitBalanceExceptions: { [orgCode: string]: number } = {};

  state: 'initializing' | 'loading' | 'ready' | 'saving';

  constructor(private accountSplitService: AccountSplitService,
              private activatedRoute: ActivatedRoute,
              private orgSvc: OrganizationService,
              private router: Router) {
    this.state = 'initializing';  
    this.columns = [
      { title: 'Account Split Id', name: 'id', hidden: true },
      { title: 'Name', name: 'name' },
      { title: 'Type', name: 'type' },          
      { title: 'Org Code', name: 'orgCode' },
      { title: 'Account', name: 'account' },
      { title: 'Percentage', name: 'percentage' }          
    ];

    this.buttons = [     
      {
        data: 'deleteManagementFeeSplit',
        iconClass: 'fas fa-trash-alt', 
        hidden: false,
        buttonClass: 'btn btn-sm px-1 py-0 btn-outline-secondary border-0',
        tooltip: 'Delete Management Fee Split'
      }
    ];

    this.accountSplitService.setAccountSplits();
    this.accountSplitService.setManagementFeeAccountSplits();
  }

  ngOnInit(): void {

    combineLatest([this.accountSplits$, 
                  this.managementFeeAccountSplits$])
                  .pipe(
                    map(([accountSplits, managementFeeAccountSplits]) => {
                     this.accountSplits = accountSplits.sort((a,b) => a.type.toLowerCase().localeCompare(b.type.toLowerCase()));
                     this.managementFeeAccountSplits = managementFeeAccountSplits;
                     this.state = 'ready';
                  }),
                  tap(() => this.filterOrg())
                ).subscribe();   

      this.openManagementFeeSplitList();
  }

  ngOnDestroy(): void {
    this.accountSplitService.closeMgntFeesAccountSplitsList();
  }

  AddToOrgClicked() {
    this.addMode = true;
  }

  AddManagementFeeAccountSplitClicked() {
    const newManagementFeesAccountSplit = new ManagementFeesAccountSplit();
    newManagementFeesAccountSplit.orgCode = this.selectedOrg?.orgCode!;
    newManagementFeesAccountSplit.accountSplitId = this.selectedAccountSplit?.id!;

    this.accountSplitService.putManagementFeeAccountSplit(newManagementFeesAccountSplit)
        .pipe(
          tap(() => this.accountSplitService.setManagementFeeAccountSplits()),
          tap(() => this.addMode = false)
        )  
        .subscribe();
  }

  public getOrgLabel(org: Organization) {
    return `${org.name} (${org.orgCode})`;
  }

  filterOrg() {       
    this.addMode = false;
    const filteredAccountSplitsIds = this.managementFeeAccountSplits?.filter(s => s.orgCode === this.selectedOrg?.orgCode).map(s => s.accountSplitId)
    this.filteredAccountSplits = this.accountSplits?.filter(a => filteredAccountSplitsIds!.includes(a.id));            

    const exps = this.filteredAccountSplits?.filter(f => f.type === 'Expense').map(f => {
      return { org: f.orgCode, acct: f.account, pct: f.percentage} as AccountSplit;
    });    

    const revs = this.filteredAccountSplits?.filter(f => f.type === 'Revenue').map(f => {
      return { org: f.orgCode, acct: f.account, pct: f.percentage} as AccountSplit;
    });

    this.isBalanced = this.checkSplitsBalance( this.selectedOrg?.orgCode!, exps!, revs!)
    this.revsTotal = revs?.reduce((sum, rev) => sum + rev.pct!, 0)!;
    this.expsTotal = exps?.reduce((sum, exp) => sum + exp.pct!, 0)!;
  }
  

  rowClicked(event: any) {
    if (event.button) {
      if (event.button.data === 'deleteManagementFeeSplit') {
        if (confirm('Are you sure you want to delete this Management Fee Split Assignment?')) {
          const row = event.row;
          this.accountSplitService.deleteManagementFeeAccountSplit(this.selectedOrg?.orgCode!, event.row.id)
              .pipe(
                tap(() => this.accountSplitService.setManagementFeeAccountSplits()),
              )
              .subscribe();
        }
      }
    }
  }

  openManagementFeeSplitList() {
    this.router.navigate(['mgnt-fees-account-splits-list'], { relativeTo: this.activatedRoute });
  }  

  private checkSplitsBalance(orgCode: string, expenseSplits: AccountSplit[], revenueSplits: AccountSplit[]) {
    const expensePct = expenseSplits.reduce((p, c) => c.pct! + p, 0);
    const revenuePct = revenueSplits.reduce((p, c) => c.pct! + p, 0);

    const isBalanced = expensePct - revenuePct === (this.splitBalanceExceptions[orgCode] || 0);
    return isBalanced;    
  }
}

class AccountSplit {
  /** optional orgCode where values are applied to the split */
  org?: string;
  /** account code for split */
  acct?: string;
  /** percentage for split; .04 = 4% */
  pct?: number;
}